// @ts-strict-ignore
import {
	Screen,
	ITEM_SUBTYPE_COLLECTION_LIST,
	ITEM_SUBTYPE_COLLECTION_TOUR,
	ITEM_SUBTYPE_COLLECTION_MENU,
	ITEM_SUBTYPE_SCREEN_PANORAMA,
	ITEM_SUBTYPE_SCREEN_STORY,
	ITEM_SUBTYPE_SCREEN_WEB,
	ITEM_TYPE_COLLECTION_ITEM,
	Collection,
} from 'mytours-read-common'
import {
	Form,
	GetCollectionItemsIndexResponseData,
	GetCollectionsShowResponse,
	MediaItem,
} from 'mytours-read-api-typescript'

import NavigationService from '../navigation/NavigationService'

import {
	setDataToLocalStorage,
	getDataFromLocalStorage,
	removeDataFromLocalStorage,
	getStyleProperty,
} from '../platform'
import { fade } from 'common_lib/services/color'

import { SectionItemType } from './types'
import { BaseUrlEnum, generateFilePath } from '../../common_lib/utils'
import { ionHistory } from '../navigation'
import { GetScreensShowResponse, GetScreensShowResponseSections } from 'mytours-read-api-typescript'
import { Redirect } from 'react-router'
import React, { ReactElement } from 'react'
import { get404Url, getSettingsUrl, getProfileUrl } from '../navigation/routes'
import strings from '../../strings'

export const AVAILABLE_SCREEN_SECTIONS = [
	GetScreensShowResponseSections.SectionTypeEnum.ImageSlider,
	GetScreensShowResponseSections.SectionTypeEnum.LinkGroup,
	GetScreensShowResponseSections.SectionTypeEnum.Location,
	GetScreensShowResponseSections.SectionTypeEnum.MediaGroup,
	GetScreensShowResponseSections.SectionTypeEnum.Menu,
	GetScreensShowResponseSections.SectionTypeEnum.OpeningTimeGroup,
	GetScreensShowResponseSections.SectionTypeEnum.PriceGroup,
	GetScreensShowResponseSections.SectionTypeEnum.SocialGroup,
	GetScreensShowResponseSections.SectionTypeEnum.Text,
	GetScreensShowResponseSections.SectionTypeEnum.QuizQuestion,
	GetScreensShowResponseSections.SectionTypeEnum.QuizScore,
	GetScreensShowResponseSections.SectionTypeEnum.SingleMedia,
	GetScreensShowResponseSections.SectionTypeEnum.BadgeGroup,
	GetScreensShowResponseSections.SectionTypeEnum.Form,
	GetScreensShowResponseSections.SectionTypeEnum.GooglePlace,
]

/**
 * Saves an item to disk so can be loaded faster next time without hitting network.
 *
 * @param item item to save to disk.
 */
export function saveScreenToDisk(
	projectId: string,
	itemId: number,
	itemVersion: number,
	language: string,
	item: Screen,
): Promise<void> {
	const key = projectId + '_screen_' + itemId + '_' + language + '_' + itemVersion
	return setDataToLocalStorage(key, item)
}

/**
 * Attempts to load a Screen from disk. Parses into CollectionItem/Screen type.
 *
 * @param itemId id of collection to load from disk
 */
export function loadScreenFromDisk(
	projectId: string,
	itemId: number,
	itemVersion: number,
	language: string,
): Promise<Screen | undefined> {
	const key = projectId + '_screen_' + itemId + '_' + language + '_' + (itemVersion || '')
	return getDataFromLocalStorage(key, false, !itemVersion)
}

/**
 * Removes an item that is persisted on disk.
 *
 * @param itemId the id of item to remove from disk
 */
export function removeScreenFromDisk(projectId: string, itemId: number, language: string): Promise<void> {
	const key = projectId + '_screen_' + itemId + '_' + language + '_'
	return removeDataFromLocalStorage(key)
}

/**
 * Check file exists for given path.
 * @param path The file path to check exists.
 */
export function checkFileExists(_path: string): Promise<boolean> {
	// Mobile web has no disk cache, so nothing is ever already on disk.
	return Promise.resolve(false)
}

/**
 * Saves an media item to disk so can be loaded faster next time without hitting network.
 * @param item item to save to disk.
 */
export function saveMediaItemToDisk(
	projectId: string,
	itemId: number,
	itemVersion: number,
	language: string,
	item: MediaItem,
): Promise<void> {
	const key = projectId + '_media_item_' + itemId + '_' + language + '_' + itemVersion
	return setDataToLocalStorage(key, item)
}

/**
 * Attempts to load a Media Item from disk.
 * @param itemId id of media item to load from disk
 */
export function loadMediaItemFromDisk(
	projectId: string,
	itemId: number,
	itemVersion: number,
	language: string,
): Promise<MediaItem | undefined> {
	const key = projectId + '_media_item_' + itemId + '_' + language + '_' + itemVersion
	return getDataFromLocalStorage(key, false, !itemVersion)
}

/**
 * Removes a media item that is persisted on disk.
 * @param itemId id of media item to delete from disk
 */
export function removeMediaItemFromDisk(projectId: string, itemId: number, language: string): Promise<void> {
	const key = projectId + '_media_item_' + itemId + '_' + language + '_'
	return removeDataFromLocalStorage(key)
}

/**
 * Saves a form to disk so can be loaded faster next time without hitting network.
 * @param item item to save to disk.
 */
export function saveFormToDisk(
	projectId: string,
	id: number,
	version: number,
	language: string,
	item: Form,
): Promise<void> {
	const key = projectId + '_form_' + id + '_' + language + '_' + version
	return setDataToLocalStorage(key, item)
}

/**
 * Attempts to load a form from disk.
 * @param id id of form to load from disk
 */
export function loadFormFromDisk(
	projectId: string,
	id: number,
	version: number,
	language: string,
): Promise<Form | undefined> {
	const key = projectId + '_form_' + id + '_' + language + '_' + version
	return getDataFromLocalStorage(key, false, !version)
}

/**
 * Removes a form that is persisted on disk.
 * @param id id of form to delete from disk
 */
export function removeFormFromDisk(projectId: string, id: number, language: string): Promise<void> {
	const key = projectId + '_form_' + id + '_' + language + '_'
	return removeDataFromLocalStorage(key)
}

export const linksData = {
	url: {
		icon: 'fas fa-globe-americas',
		link: '',
		prefix: '',
	},
	email: {
		icon: 'fas fa-at',
		link: 'mailto:',
		prefix: '',
	},
	phone: {
		icon: 'fas fa-phone-alt',
		link: 'tel:',
		prefix: '',
	},
	internal: {
		icon: 'fas fa-link',
		link: '',
		prefix: '',
	},
	facebook: {
		icon: 'fab fa-facebook',
		link: 'https://www.facebook.com/',
		title: strings.follow_us_on_facebook,
		prefix: '',
	},
	instagram: {
		icon: 'fab fa-instagram',
		link: 'https://www.instagram.com/',
		prefix: '@',
	},
	twitter: {
		icon: 'fab fa-x-twitter',
		link: 'https://twitter.com/',
		prefix: '@',
	},
	pinterest: {
		icon: 'fab fa-pinterest',
		link: 'https://www.pinterest.com/',
		prefix: '@',
	},
	whatsapp: {
		icon: 'fab fa-whatsapp',
		link: 'https://wa.me/',
		prefix: '@',
	},
	wechat: {
		icon: 'fab fa-weixin',
		link: 'https://wechat.com/',
		prefix: '@',
	},
	youtube: {
		icon: 'fab fa-youtube',
		link: 'https://www.youtube.com/channel/',
		title: 'YouTube',
		prefix: '',
	},
	vimeo: {
		icon: 'fab fa-vimeo-v',
		link: 'https://vimeo.com/',
		prefix: '@',
	},
	linkedin: {
		icon: 'fab fa-linkedin-in',
		link: 'https://www.linkedin.com/in/',
		prefix: '@',
	},
	tiktok: {
		icon: 'fab fa-tiktok',
		link: 'https://www.tiktok.com/@',
		prefix: '@',
	},
	weibo: {
		icon: 'fab fa-weibo',
		link: '',
		prefix: '',
	},
	bluesky: {
		icon: 'fab fa-bluesky',
		link: 'https://bsky.app/profile/',
		prefix: '@',
	},
	// These three resolve through the active locale, so they have to be read per access rather
	// than frozen when this module loads: at load time no language is selected yet, which
	// pinned them to the wrong locale for the rest of the session. Deferring also keeps the
	// route builders out of module evaluation, which would otherwise reach back into a
	// half-initialised `navigation/routes` and read them as undefined.
	settings: {
		icon: 'fas fa-gear',
		get link() {
			return getSettingsUrl()
		},
		prefix: '',
	},
	badges: {
		icon: 'fas fa-badge-check',
		get link() {
			return getProfileUrl()
		},
		prefix: '',
	},
	favourites: {
		icon: 'fas fa-heart',
		get link() {
			return getProfileUrl()
		},
		prefix: '',
	},
}

const isURL = (username: string): boolean => {
	const match = /^https?:\/\//gy.exec(username)
	return !!match
}

export const getSocialLink = (username: string, link: string): string => {
	if (isURL(username)) {
		return username
	}

	return link + username
}

export const getSocialLinkTitle = (type: string, username: string): string => {
	const data = linksData[type]

	if (isURL(username)) {
		return getSocialLink(username, data.link).split('/').reverse()[0] // TODO: temp fix until we don't have username from the backend
	}

	return data.title || data.prefix + username
}

export const generateZipUrl = (id: string, region: string): string => {
	const { base, path } = generateFilePath(id.toString(), BaseUrlEnum.BunnyUploads, {
		pathType: 'zip',
		region,
	})
	return base + path + 'index.html'
}

export const onLinkAction = (
	subtype: string,
	itemId: number,
	itemVersion: number,
	openModalCallback: () => void,
	closeModalCallback?: () => void,
	collection?: Collection,
	collectionItemsIds?: number[],
	accountId?: number,
): void => {
	const type = (subtype && subtype.split('_').reverse()[0]) || ''

	if (
		[ITEM_SUBTYPE_COLLECTION_TOUR, ITEM_SUBTYPE_COLLECTION_LIST, ITEM_SUBTYPE_COLLECTION_MENU].includes(subtype as any) // types issue
	) {
		closeModalCallback?.()

		NavigationService.navigateToItem({
			type,
			itemId,
			itemVersion,
			collectionVersion: itemVersion,
			accountId,
			includeTabIndex: true,
		})
	} else if (
		collection &&
		collection.subtype !== GetCollectionsShowResponse.SubtypeEnum.CollectionTour &&
		collectionItemsIds?.includes(itemId)
	) {
		const collectionType =
			collection?.subtype === GetCollectionsShowResponse.SubtypeEnum.CollectionMenu
				? GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Menu
				: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.List

		const params = {
			collectionId: collection.id,
			collectionVersion: collection.version,
			accountId,
			itemId: itemId,
			collectionTitle: collection.title,
			title: collection.title,
		}

		NavigationService.navigateToCollectionItems({
			params,
			collectionType,
			itemType: GetCollectionItemsIndexResponseData.ItemSubtypeEnum.Story,
		})
	} else if (subtype === ITEM_SUBTYPE_SCREEN_STORY) {
		NavigationService.navigateToScreenStory({ itemId, accountId })
	} else if (subtype === ITEM_SUBTYPE_SCREEN_PANORAMA) {
		NavigationService.navigateToScreenPanorama({ itemId, accountId })
	} else if (subtype === ITEM_SUBTYPE_SCREEN_WEB) {
		NavigationService.navigateToScreenWeb({ itemId, accountId })
	} else {
		openModalCallback()
	}
}

export const getSectionSubtypeAndId = (sectionItem: SectionItemType): { subtype: string; id: number } => {
	const isCollectionItem = sectionItem && sectionItem.item_type === ITEM_TYPE_COLLECTION_ITEM
	const subtype = sectionItem?.[isCollectionItem ? 'collection_item_subtype' : 'item_subtype']
	const id = sectionItem ? (isCollectionItem ? sectionItem.collection_item_id : sectionItem.item_id) : undefined

	return {
		subtype,
		id,
	}
}

export const updateTranscriptionHash = (value: boolean, prevModalState: boolean, callback: () => void) => {
	if (!prevModalState || typeof value === 'boolean') {
		if (value) {
			callback()

			window.location.hash = '#transcript'
		} else {
			ionHistory.replace(ionHistory.location.pathname)
		}
	} else {
		ionHistory.replace(ionHistory.location.pathname)
	}
}

export const renderScreenRedirect = (
	expectedScreenType: GetScreensShowResponse.ScreenTypeEnum,
	screenType: GetScreensShowResponse.ScreenTypeEnum,
): ReactElement | undefined => {
	if (!screenType) {
		return null
	}

	switch (screenType) {
		case GetScreensShowResponse.ScreenTypeEnum.Panorama:
			if (
				expectedScreenType === GetScreensShowResponse.ScreenTypeEnum.Panorama ||
				!ionHistory.location.pathname.includes(expectedScreenType)
			) {
				return null
			}

			return (
				<Redirect
					to={ionHistory.location.pathname.replace(expectedScreenType, GetScreensShowResponse.ScreenTypeEnum.Panorama)}
				/>
			)
		case GetScreensShowResponse.ScreenTypeEnum.Web:
			if (
				expectedScreenType === GetScreensShowResponse.ScreenTypeEnum.Web ||
				!ionHistory.location.pathname.includes(expectedScreenType)
			) {
				return null
			}

			return (
				<Redirect
					to={ionHistory.location.pathname.replace(expectedScreenType, GetScreensShowResponse.ScreenTypeEnum.Web)}
				/>
			)
		case GetScreensShowResponse.ScreenTypeEnum.Story:
			if (
				expectedScreenType === GetScreensShowResponse.ScreenTypeEnum.Story ||
				!ionHistory.location.pathname.includes(expectedScreenType)
			) {
				return null
			}

			return (
				<Redirect
					to={ionHistory.location.pathname.replace(expectedScreenType, GetScreensShowResponse.ScreenTypeEnum.Story)}
				/>
			)
		default:
			return <Redirect to={get404Url()} />
	}
}

export const getCollapseShadowStyle = (): string => {
	const bgColor = getStyleProperty('--background-color')
	return `linear-gradient(180deg,
		${fade(bgColor, 0)} 0,
		${fade(bgColor, 0.02)} 0%,
		${fade(bgColor, 0.05)} 2%,
		${fade(bgColor, 0.12)} 4%,
		${fade(bgColor, 0.2)} 6%,
		${fade(bgColor, 0.29)} 9%,
		${fade(bgColor, 0.39)} 13%,
		${fade(bgColor, 0.5)} 17%,
		${fade(bgColor, 0.61)} 22%,
		${fade(bgColor, 0.71)} 26%,
		${fade(bgColor, 0.8)} 31%,
		${fade(bgColor, 0.88)} 36%,
		${fade(bgColor, 0.95)} 41%,
		${fade(bgColor, 0.98)} 45%,
		${bgColor})`
}
